import { useEffect, useState } from "react";
import { PrescriptionDetail } from "../../../../../services/api/OrderService/types";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

export const ViewPrescription = ({
  prescription,
  onClose,
}: {
  prescription: PrescriptionDetail[];
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = () => {
    if (isTransitioning || prescription.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % prescription.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const prevSlide = () => {
    if (isTransitioning || prescription.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(
      (prev) => (prev - 1 + prescription.length) % prescription.length
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const openPreview = (index: number) => {
    setPreviewIndex(index);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const nextPreview = () => {
    setPreviewIndex((prev) => (prev + 1) % prescription.length);
  };

  const prevPreview = () => {
    setPreviewIndex(
      (prev) => (prev - 1 + prescription.length) % prescription.length
    );
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPreview) {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            prevPreview();
            break;
          case "ArrowRight":
            e.preventDefault();
            nextPreview();
            break;
          case "Escape":
            e.preventDefault();
            closePreview();
            break;
        }
      } else {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            prevSlide();
            break;
          case "ArrowRight":
            e.preventDefault();
            nextSlide();
            break;
          case "Escape":
            e.preventDefault();
            onClose();
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showPreview, previewIndex, currentIndex, onClose]);

  if (prescription.length === 0) {
    return (
      <div className="bg-gray-900 fixed h-screen w-full top-0 left-0 flex items-center justify-center z-50">
        <div className="text-center">
          <p className="text-white text-xl mb-4">No prescriptions available</p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Container */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 z-50 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-black/30 backdrop-blur-sm">
          <h2 className="text-white text-xl font-semibold">
            Prescription {currentIndex + 1} of {prescription.length}
          </h2>
          <button
            onClick={onClose}
            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-all duration-200 hover:scale-110"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Card Carousel */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="relative">
            {/* Card Container */}
            <div className="relative w-80 h-80">
              {prescription.map((data, index) => (
                <div
                  key={data.id}
                  className={`absolute inset-0 transition-all duration-500 ease-in-out cursor-pointer ${index === currentIndex
                      ? "opacity-100 transform translate-x-0 scale-100 z-10"
                      : index < currentIndex
                        ? "opacity-30 transform -translate-x-20 scale-90 z-0"
                        : "opacity-30 transform translate-x-20 scale-90 z-0"
                    }`}
                  onClick={() => openPreview(index)}
                >
                  <div className="bg-white rounded-xl shadow-2xl overflow-hidden h-full group hover:shadow-3xl transition-shadow duration-300">
                    {/* Image */}
                    <div className="h-64 bg-gray-100 flex items-center justify-center overflow-hidden">
                      <img
                        src={data.prescriptionImage}
                        alt={data.source}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://via.placeholder.com/300x240/374151/9CA3AF?text=Prescription+Image";
                        }}
                      />
                    </div>

                    {/* Card Info */}
                    <div className="p-4 h-16 flex flex-col justify-center bg-white">
                      <p className="text-gray-800 font-medium text-sm text-center truncate">
                        {data.source}
                      </p>
                      {data.note && (
                        <p className="text-gray-600 text-xs text-center truncate mt-1">
                          {data.note}
                        </p>
                      )}
                    </div>

                    {/* Zoom indicator */}
                    <div className="absolute top-2 right-2 bg-black/50 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ZoomIn className="text-white w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {prescription.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  disabled={isTransitioning}
                  className="absolute left-[-60px] top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  disabled={isTransitioning}
                  className="absolute right-[-60px] top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Indicators */}
        {prescription.length > 1 && (
          <div className="flex justify-center items-center space-x-3 p-6 bg-black/20 backdrop-blur-sm">
            {prescription.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isTransitioning) {
                    setIsTransitioning(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsTransitioning(false), 300);
                  }
                }}
                disabled={isTransitioning}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
                    ? "bg-white scale-125 shadow-lg"
                    : "bg-white/40 hover:bg-white/70 hover:scale-110"
                  }`}
              />
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="text-center p-4 bg-black/20 backdrop-blur-sm">
          <p className="text-white/60 text-sm">
            Click on card to view full image • Use arrow keys to navigate •
            Press ESC to close
          </p>
        </div>
      </div>

      {/* Scrollable Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] backdrop-blur-sm"
          onClick={closePreview}
        >
          <div className="h-full overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <div
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={closePreview}
                  className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm z-10"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Image */}
                <img
                  src={prescription[previewIndex].prescriptionImage}
                  alt={prescription[previewIndex].source}
                  className="w-full h-auto rounded-lg shadow-2xl"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/800x600/374151/9CA3AF?text=Prescription+Image";
                  }}
                />

                {/* Navigation buttons */}
                {prescription.length > 1 && (
                  <>
                    <button
                      onClick={prevPreview}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextPreview}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Image info */}
                <div className="mt-4 bg-black/80 p-6 rounded-lg backdrop-blur-sm">
                  <h3 className="text-white font-semibold text-lg text-center mb-2">
                    {prescription[previewIndex].source}
                  </h3>
                  {prescription[previewIndex].note && (
                    <p className="text-white/80 text-center mb-3">
                      {prescription[previewIndex].note}
                    </p>
                  )}
                  <p className="text-white/60 text-sm text-center">
                    Image {previewIndex + 1} of {prescription.length}
                  </p>
                  <p className="text-white/40 text-xs text-center mt-2">
                    Press ESC to close • Click outside image to close • Scroll
                    to see full image
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
